/**
 * Timeline Clip Component (canvas-v2)
 *
 * Individual clip block with thumbnails that can be dragged to reorder.
 * Supports click to select, drag edges to trim.
 * V2 additions: 1:1 square thumbnails (width = clip height), speed badge when speed !== 1.0.
 *
 * Memoized for performance with large numbers of clips.
 */

import { useCallback, useState, useRef, memo, useEffect, useSyncExternalStore } from 'react'
import type { TimelineClip as TimelineClipData } from './types'
import {
  getClipWidth,
  pixelsToSeconds,
  secondsToPixels,
  getClipPosition,
  findSnapPosition,
  findEdgeSnapPosition,
} from './timelineUtils'
import { StaticWaveform } from './StaticWaveform'
import { useClipThumbnails } from './useClipThumbnails'
import {
  getMultiDragPreview,
  setMultiDragPreview,
  subscribeMultiDragPreview,
} from './multiDragPreviewStore'
import createDebug from 'debug'
import { createNadDebug } from '@ponpon/api-client'

const debug = createDebug('dw:timeline-playback:video-waveform')
const interactionLog = createNadDebug('dw:timeline-playback:render-contract')

// RFC-0633 (Issue 4): one-shot log of the clip body strip ratios so we can
// confirm the round-4 80/20 split is in effect from the run log.
let stripRatioLogged = false
function logStripRatioOnce(thumb: number, waveform: number) {
  if (stripRatioLogged) return
  stripRatioLogged = true
  interactionLog({ event: 'clip-strip-ratio', thumb, waveform })
}
const selectionLog = createNadDebug('dw:timeline-playback:render-contract')

interface TimelineClipProps {
  /** Clip data */
  clip: TimelineClipData

  /** All clips (for calculating position) */
  allClips: TimelineClipData[]

  /** Zoom level (pixels per second) */
  zoomLevel: number

  /** Whether this clip is selected */
  isSelected: boolean

  /** Whether this clip is part of an active multi-selection (>1 selected) */
  isMultiSelectionMember?: boolean

  /** Track height in pixels (used for 1:1 square thumbnails) */
  trackHeight: number

  /** Callback when clip is clicked (replace selection) */
  onSelect: (clipId: string) => void

  /** Callback when clip is shift-clicked (toggle membership in multi-selection) */
  onToggleSelect?: (clipId: string) => void

  /** Multi-drag commit: applies a uniform startOffset delta + optional track delta to ALL selected clips */
  onMultiDragOffset?: (deltaSec: number, trackDelta?: number) => void

  /** Callback when clip is reordered */
  onReorder: (clipId: string, newOrder: number) => void

  /** Callback when clip startOffset changes */
  onOffsetChange?: (clipId: string, startOffset: number) => void

  /** Callback when clip track index changes */
  onTrackIndexChange?: (clipId: string, trackIndex: number) => void

  /** Callback when clip is trimmed */
  onTrim: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Callback for live trim preview (no history push) */
  onTrimPreview?: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Callback when snap position changes during drag */
  onSnapPositionChange?: (position: number | null) => void

  /** Callback fired on mouseUp after trim-start commit to shift timeline if any clip went negative. */
  onNormalizeTimelineOrigin?: () => void

  /** Playhead position in pixels (for edge snap, Issue 2). */
  playheadPixel?: number

  /** Whether timeline is locked (no editing) */
  locked?: boolean

  /** Number of video tracks for cross-track drag bounds */
  videoTrackCount?: number
}

export const TimelineClipComponent = memo(function TimelineClipComponent({
  clip,
  allClips,
  zoomLevel,
  isSelected,
  isMultiSelectionMember = false,
  trackHeight,
  onSelect,
  onToggleSelect,
  onMultiDragOffset,
  onReorder,
  onOffsetChange,
  onTrackIndexChange,
  onTrim,
  onTrimPreview,
  onSnapPositionChange,
  onNormalizeTimelineOrigin,
  playheadPixel,
  locked = false,
  videoTrackCount = 1,
}: TimelineClipProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [dragType, setDragType] = useState<'move' | 'trim-start' | 'trim-end' | null>(null)
  const [dragOffset, setDragOffset] = useState(0)
  const [dragOffsetY, setDragOffsetY] = useState(0)
  const dragStartXRef = useRef(0)
  const dragStartYRef = useRef(0)
  const dragOffsetRef = useRef(0)
  const dragOffsetYRef = useRef(0)
  // Trim drag bookkeeping:
  //  - lastTrimRef: current accumulated trim values (committed on mouseUp)
  //  - originalTrimStartRef / originalStartOffsetRef: snapshots at mousedown
  //    so trim-start can keep `startOffset = originalStartOffset + (newTrimStart - originalTrimStart)`
  const lastTrimRef = useRef<{ trimStart: number; trimEnd: number; startOffset?: number } | null>(null)
  const originalTrimStartRef = useRef(0)
  const originalStartOffsetRef = useRef(0)
  const trimStartCommittedRef = useRef(false)
  const clipRef = useRef<HTMLDivElement>(null)

  const width = getClipWidth(clip, zoomLevel)
  const EDGE_RESIZE_WIDTH = 8 // pixels for edge resize handles

  // RFC-0633 (Issue 4): thumbnail strip occupies the top 80% of the inner
  // clip body, waveform overlay the bottom 20%. Previously 65/35 — round 4
  // shrinks the waveform so trimmed thumbs aren't squashed.
  const THUMB_STRIP_RATIO = 0.80
  const WAVEFORM_STRIP_RATIO = 0.20
  logStripRatioOnce(THUMB_STRIP_RATIO, WAVEFORM_STRIP_RATIO)
  // RFC-0633 (Issue 5): tiles are fixed 16:9. Height is the top 80% of the
  // inner clip body, width follows `height * 16/9`. The thumbnail hook owns
  // tile count, source-time selection, and the per-source cache.
  const innerClipHeight = Math.max(trackHeight - 16, 32)
  const thumbHeight = Math.max(20, Math.round(innerClipHeight * THUMB_STRIP_RATIO))
  const { tiles, thumbWidth } = useClipThumbnails(clip, {
    zoomPxPerSec: zoomLevel,
    thumbHeight,
  })

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, type: 'move' | 'trim-start' | 'trim-end') => {
      if (locked) return
      // Only respond to primary (left) mouse button. Right/middle must NOT drag.
      if (e.button !== 0) {
        interactionLog({
          event: 'clip-drag-rejected',
          clipId: clip.id,
          button: e.button,
          type,
        })
        return
      }

      e.preventDefault()
      e.stopPropagation()

      // RFC-0651 #2: while a trim drag is in flight, the timeline area's
      // click-to-set-playhead handler must ignore the trailing pointerup /
      // click that fires when the user releases outside the clip body. We
      // signal this via a shared window flag (kept simple to avoid threading
      // a ref through TimelineEditorModal). Cleared on pointerup below.
      if (type === 'trim-start' || type === 'trim-end') {
        try {
          window.__dwTimelineTrimActive = true
        } catch {
          // ignore (e.g. cross-origin frames)
        }
      }

      setIsDragging(true)
      setDragType(type)
      dragStartXRef.current = e.clientX
      dragStartYRef.current = e.clientY
      dragOffsetRef.current = 0
      dragOffsetYRef.current = 0

      // Snapshot whether this clip was part of an active multi-selection at
      // drag start. We capture this once so the user can begin a drag from
      // any selected clip without disturbing the selection. If a non-selected
      // clip is mousedown'd, we replace the selection with that clip first.
      const dragAsMultiSelection = isMultiSelectionMember
      if (type === 'move' && !isSelected && !e.shiftKey) {
        onSelect(clip.id)
      }

      if (type === 'move' && dragAsMultiSelection) {
        setMultiDragPreview({ activeClipId: clip.id, deltaPx: 0, deltaPxY: 0 })
        interactionLog({
          event: 'multi-drag-preview-begin',
          activeClipId: clip.id,
          clipType: 'video',
        })
      }

      // Initialize lastTrimRef at drag start so we accumulate from the current values
      if (type === 'trim-start' || type === 'trim-end') {
        lastTrimRef.current = { trimStart: clip.trimStart, trimEnd: clip.trimEnd, startOffset: clip.startOffset }
        originalTrimStartRef.current = clip.trimStart
        originalStartOffsetRef.current = clip.startOffset
        trimStartCommittedRef.current = false
      }

      // mousedown anchor for trim drags — we recompute deltas from this point
      // (not incrementally) so snap can re-derive the snapped delta from the
      // ORIGINAL mousedown position rather than from a moving anchor.
      const trimDragStartX = e.clientX

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - dragStartXRef.current
        const deltaY = moveEvent.clientY - dragStartYRef.current

        if (type === 'move') {
          dragOffsetRef.current = deltaX
          dragOffsetYRef.current = deltaY
          setDragOffset(deltaX)
          setDragOffsetY(deltaY)

          // Calculate current position of clip
          const currentClipPosition = getClipPosition(clip, allClips, zoomLevel)
          const newPosition = currentClipPosition + deltaX

          // Check for snap position (only for the grabbed clip, not the
          // multi-drag set — preview clips follow the grabbed clip's delta).
          const otherClips = allClips.filter((c) => c.id !== clip.id)
          const snapPos = findSnapPosition(clip, otherClips, newPosition, zoomLevel)

          // Notify parent of snap position
          if (onSnapPositionChange) {
            onSnapPositionChange(snapPos)
          }

          // If snapping, adjust drag offset to snap position
          if (snapPos !== null) {
            const snappedOffset = snapPos - currentClipPosition
            dragOffsetRef.current = snappedOffset
            setDragOffset(snappedOffset)
          }

          // Broadcast the live delta so other selected clips can render the
          // same transform during drag (multi-drag visual feedback).
          if (dragAsMultiSelection) {
            setMultiDragPreview({
              activeClipId: clip.id,
              deltaPx: dragOffsetRef.current,
              deltaPxY: dragOffsetYRef.current,
            })
          }
        } else if (type === 'trim-start') {
          // Trim start (Issue 1 + Issue 2):
          //  - Couple startOffset to trimStart so the RIGHT edge stays fixed
          //    and frames are anchored to source-video time.
          //  - Allow temporarily-negative startOffset; NORMALIZE_TIMELINE_ORIGIN
          //    on mouseUp shifts all clips back to >= 0.
          //  - Snap left edge to playhead pixel and to other clips' edges on
          //    the same track (within ~8px).
          const absoluteDeltaPx = moveEvent.clientX - trimDragStartX
          const deltaSec = pixelsToSeconds(absoluteDeltaPx, zoomLevel)
          const originalTrimStart = originalTrimStartRef.current
          const originalStartOffset = originalStartOffsetRef.current

          // Upper bound: trim-start can't pass right-edge minus min-gap.
          const maxTrimStart = clip.originalDuration - clip.trimEnd - 0.1
          // Lower bound: trimStart must be >= 0 (can't go before source frame 0).
          // The deltaSec that takes trimStart to 0 from originalTrimStart is -originalTrimStart.
          let newTrimStart = Math.max(0, Math.min(originalTrimStart + deltaSec, maxTrimStart))

          // The clamped delta we actually applied to trimStart.
          let appliedDelta = newTrimStart - originalTrimStart
          let newStartOffset = originalStartOffset + appliedDelta

          // Edge snap: try snapping the clip's LEFT EDGE pixel.
          // Left edge pixel = secondsToPixels(newStartOffset, zoomLevel)
          const sameTrackOthers = allClips.filter(
            (c) => c.trackIndex === clip.trackIndex && c.id !== clip.id
          )
          const leftEdgePx = secondsToPixels(newStartOffset, zoomLevel)
          const snap = findEdgeSnapPosition(
            leftEdgePx,
            sameTrackOthers,
            zoomLevel,
            playheadPixel ?? null
          )
          if (snap) {
            const snappedStartOffset = pixelsToSeconds(snap.snapPx, zoomLevel)
            // newTrimStart corresponds to snappedStartOffset via the lockstep:
            // appliedDelta = snappedStartOffset - originalStartOffset
            // newTrimStart = originalTrimStart + appliedDelta
            const desiredTrimStart = originalTrimStart + (snappedStartOffset - originalStartOffset)
            // Only accept snap if it doesn't violate trim bounds.
            if (desiredTrimStart >= 0 && desiredTrimStart <= maxTrimStart) {
              newTrimStart = desiredTrimStart
              newStartOffset = snappedStartOffset
              appliedDelta = newTrimStart - originalTrimStart
              if (onSnapPositionChange) {
                onSnapPositionChange(snap.snapPx)
              }
              interactionLog({
                event: 'edge-snap-applied',
                clipId: clip.id,
                edge: 'start',
                snapTarget: snap.target,
                snapToPx: snap.snapPx,
                snapToSec: snappedStartOffset,
              })
            } else if (onSnapPositionChange) {
              onSnapPositionChange(null)
            }
          } else if (onSnapPositionChange) {
            onSnapPositionChange(null)
          }

          const currentTrimEnd = lastTrimRef.current?.trimEnd ?? clip.trimEnd
          lastTrimRef.current = {
            trimStart: newTrimStart,
            trimEnd: currentTrimEnd,
            startOffset: newStartOffset,
          }
          const trimPreview = onTrimPreview || onTrim
          trimPreview(clip.id, newTrimStart, currentTrimEnd, newStartOffset)
        } else if (type === 'trim-end') {
          // Trim end (Issue 2 — snap support; startOffset is unchanged):
          const absoluteDeltaPx = moveEvent.clientX - trimDragStartX
          const deltaSec = pixelsToSeconds(-absoluteDeltaPx, zoomLevel)
          const originalTrimEnd = clip.trimEnd // snapshot at mousedown
          const currentTrimStart = lastTrimRef.current?.trimStart ?? clip.trimStart
          const startOffsetForEnd = lastTrimRef.current?.startOffset ?? clip.startOffset

          const maxTrimEnd = clip.originalDuration - currentTrimStart - 0.1
          let newTrimEnd = Math.max(0, Math.min(originalTrimEnd + deltaSec, maxTrimEnd))

          // Right edge pixel = secondsToPixels(startOffset, zoomLevel) + width
          // width depends on effectiveDuration which depends on trims.
          const effDurForSnap =
            (clip.originalDuration - currentTrimStart - newTrimEnd) / clip.speed
          const rightEdgePx = secondsToPixels(startOffsetForEnd + effDurForSnap, zoomLevel)
          const sameTrackOthers = allClips.filter(
            (c) => c.trackIndex === clip.trackIndex && c.id !== clip.id
          )
          const snap = findEdgeSnapPosition(
            rightEdgePx,
            sameTrackOthers,
            zoomLevel,
            playheadPixel ?? null
          )
          if (snap) {
            const desiredRightEdgeSec = pixelsToSeconds(snap.snapPx, zoomLevel)
            const desiredEffDur = desiredRightEdgeSec - startOffsetForEnd
            // newTrimEnd from: (originalDuration - currentTrimStart - newTrimEnd) / speed = desiredEffDur
            const desiredTrimEnd =
              clip.originalDuration - currentTrimStart - desiredEffDur * clip.speed
            if (desiredTrimEnd >= 0 && desiredTrimEnd <= maxTrimEnd) {
              newTrimEnd = desiredTrimEnd
              if (onSnapPositionChange) {
                onSnapPositionChange(snap.snapPx)
              }
              interactionLog({
                event: 'edge-snap-applied',
                clipId: clip.id,
                edge: 'end',
                snapTarget: snap.target,
                snapToPx: snap.snapPx,
                snapToSec: desiredRightEdgeSec,
              })
            } else if (onSnapPositionChange) {
              onSnapPositionChange(null)
            }
          } else if (onSnapPositionChange) {
            onSnapPositionChange(null)
          }

          lastTrimRef.current = {
            trimStart: currentTrimStart,
            trimEnd: newTrimEnd,
            startOffset: startOffsetForEnd,
          }
          const trimPreview = onTrimPreview || onTrim
          trimPreview(clip.id, currentTrimStart, newTrimEnd, startOffsetForEnd)
        }
      }

      const handleMouseUp = () => {
        // Always end the multi-drag preview before the commit so other
        // selected clips snap back to their stored positions in the same
        // frame as the reducer's commit (or unchanged-state, for tiny drags).
        if (type === 'move' && dragAsMultiSelection) {
          interactionLog({
            event: 'multi-drag-preview-end',
            activeClipId: clip.id,
            clipType: 'video',
            committed:
              Math.abs(dragOffsetRef.current) > 5 || Math.abs(dragOffsetYRef.current) > 10,
          })
          setMultiDragPreview(null)
        }

        if (type === 'move' && (Math.abs(dragOffsetRef.current) > 5 || Math.abs(dragOffsetYRef.current) > 10)) {
          if (dragAsMultiSelection && onMultiDragOffset) {
            // Multi-drag: shift ALL selected clips by the same delta. Snap-on
            // -mouseup — the visual transform during drag only moves the
            // primary clip, but the commit applies uniformly to the set.
            const deltaSec = pixelsToSeconds(dragOffsetRef.current, zoomLevel)
            const trackDelta = videoTrackCount > 1
              ? Math.round(dragOffsetYRef.current / Math.max(1, trackHeight + 16))
              : 0
            onMultiDragOffset(deltaSec, trackDelta)
          } else {
            if (onTrackIndexChange && videoTrackCount > 1) {
              const trackDelta = Math.round(dragOffsetYRef.current / Math.max(1, trackHeight + 16))
              const nextTrack = Math.max(0, Math.min(videoTrackCount - 1, clip.trackIndex + trackDelta))
              if (nextTrack !== clip.trackIndex) {
                onTrackIndexChange(clip.id, nextTrack)
              }
            }
            if (onOffsetChange) {
              const newOffset = Math.max(0, clip.startOffset + pixelsToSeconds(dragOffsetRef.current, zoomLevel))
              onOffsetChange(clip.id, newOffset)
            } else {
              onReorder(clip.id, clip.order)
            }
          }
        }

        // Commit final trim value to history on mouseUp
        if ((type === 'trim-start' || type === 'trim-end') && lastTrimRef.current) {
          onTrim(
            clip.id,
            lastTrimRef.current.trimStart,
            lastTrimRef.current.trimEnd,
            lastTrimRef.current.startOffset
          )
          trimStartCommittedRef.current = type === 'trim-start'
          lastTrimRef.current = null
        }

        setIsDragging(false)
        setDragType(null)
        setDragOffset(0)
        setDragOffsetY(0)
        dragOffsetRef.current = 0
        dragOffsetYRef.current = 0

        // Clear snap indicator
        if (onSnapPositionChange) {
          onSnapPositionChange(null)
        }

        // After a trim-start commit, normalize the timeline origin so any
        // negative startOffset is shifted back to 0 (Issue 1 — uniform
        // shift across all video AND audio tracks).
        if (trimStartCommittedRef.current && onNormalizeTimelineOrigin) {
          onNormalizeTimelineOrigin()
          trimStartCommittedRef.current = false
        }

        // RFC-0651 #2: keep the trim-active flag set through the trailing
        // click event the timeline area receives after pointerup, then
        // clear it on the next macrotask. The timeline's click handler
        // runs synchronously after pointerup in the same frame, so a
        // setTimeout(0) defers clearing until after that click fires.
        if (type === 'trim-start' || type === 'trim-end') {
          setTimeout(() => {
            try {
              window.__dwTimelineTrimActive = false
            } catch {
              // ignore
            }
          }, 0)
        }

        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [locked, clip, allClips, zoomLevel, trackHeight, videoTrackCount, isSelected, isMultiSelectionMember, onSelect, onMultiDragOffset, onReorder, onOffsetChange, onTrackIndexChange, onTrim, onTrimPreview, onSnapPositionChange, onNormalizeTimelineOrigin, playheadPixel]
  )

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (locked || isDragging) return
      e.stopPropagation()
      if (e.shiftKey && onToggleSelect) {
        // Shift+click: toggle membership in the multi-selection. Allowed
        // across video and audio (cross-type selection — see RFC).
        onToggleSelect(clip.id)
        selectionLog({ event: 'shift-click-toggle', clipId: clip.id, clipType: 'video' })
      } else if (!isSelected) {
        // Plain click on an unselected clip: replace selection with this clip.
        onSelect(clip.id)
      } else {
        // Plain click on an already-selected clip (single OR multi-selection
        // member): preserve selection. Collapsing here would drop the rest
        // of a multi-selection on every mouseup-without-drag.
        selectionLog({
          event: 'selection-preserved-on-click',
          clipId: clip.id,
          clipType: 'video',
        })
      }
    },
    [locked, isDragging, clip.id, isSelected, onSelect, onToggleSelect]
  )

  const handleEdgeMouseDown = useCallback(
    (e: React.MouseEvent, edge: 'start' | 'end') => {
      e.stopPropagation()
      handleMouseDown(e, edge === 'start' ? 'trim-start' : 'trim-end')
    },
    [handleMouseDown]
  )

  // Subscribe to the multi-drag preview store so this clip mirrors the
  // grabbed clip's transform whenever it is a non-active member of an
  // active multi-drag. The grabbed clip ignores this and uses its local
  // dragOffset/dragOffsetY for buttery cursor-follow.
  const multiDragPreview = useSyncExternalStore(
    subscribeMultiDragPreview,
    getMultiDragPreview,
    getMultiDragPreview
  )
  const previewTransform =
    isMultiSelectionMember &&
    multiDragPreview !== null &&
    multiDragPreview.activeClipId !== clip.id
      ? `translate(${multiDragPreview.deltaPx}px, ${multiDragPreview.deltaPxY}px)`
      : null
  const isPreviewGhost = previewTransform !== null

  const transform = isDragging && dragType === 'move'
    ? `translate(${dragOffset}px, ${dragOffsetY}px)`
    : previewTransform ?? 'none'

  return (
    <div
      ref={clipRef}
      className={`
        absolute top-0 h-full
        bg-teal-700/60 border border-teal-500
        rounded
        overflow-hidden
        ${isSelected ? 'ring-2 ring-blue-400' : ''}
        ${isMultiSelectionMember ? 'ring-blue-300 shadow-[0_0_12px_rgba(96,165,250,0.45)]' : ''}
        ${isDragging && dragType === 'move' ? 'opacity-50 cursor-grabbing' : 'cursor-grab'}
        ${isPreviewGhost ? 'opacity-50' : ''}
        ${locked ? 'cursor-not-allowed' : ''}
      `}
      style={{
        width: `${width}px`,
        transform,
      }}
      data-testid="timeline-video-clip"
      data-clip-id={clip.id}
      data-track-index={clip.trackIndex}
      data-start-offset={clip.startOffset.toFixed(3)}
      data-start-sec={clip.startOffset.toFixed(3)}
      data-selected={isSelected ? 'true' : 'false'}
      data-multi-drag-preview={isPreviewGhost ? 'true' : 'false'}
      data-volume={clip.volume.toFixed(2)}
      data-muted={clip.muted ? 'true' : 'false'}
      onMouseDown={(e) => !locked && handleMouseDown(e, 'move')}
      onClick={handleClick}
    >
      {/* Thumbnail filmstrip background -- top 80% of clip, fixed 16:9 tiles
          center-cropped from the source (RFC-0633 Issue 5). The container's
          overflow:hidden clips any rightmost tile that overshoots the clip
          body — no gap, no visible overlap. */}
      {tiles.length > 0 && (
        <div
          className="absolute left-0 right-0 top-0 flex overflow-hidden pointer-events-none"
          style={{ height: `${Math.round(THUMB_STRIP_RATIO * 100)}%` }}
          data-testid="timeline-clip-thumb-strip"
          data-tile-count={tiles.length}
          data-thumb-width={thumbWidth}
          data-thumb-height={thumbHeight}
        >
          {tiles.map((tile, idx) => (
            <div
              key={`${tile.sourceTimeMs}-${idx}`}
              className="flex-shrink-0 bg-surface-2"
              style={{ width: `${tile.thumbWidth}px`, height: '100%' }}
              data-source-time-ms={tile.sourceTimeMs}
            >
              {tile.dataUrl && (
                <img
                  src={tile.dataUrl}
                  alt=""
                  draggable={false}
                  className="block"
                  style={{
                    width: `${tile.thumbWidth}px`,
                    height: '100%',
                    objectFit: 'none',
                  }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Audio waveform overlay -- bottom 35% of the clip (RFC-0651 #10) */}
      <WaveformOverlay clip={clip} stripRatio={WAVEFORM_STRIP_RATIO} />

      {/* Fade-in visualization overlay -- left edge, opacity ramp from black */}
      {(() => {
        const fadeSec = Math.max(0, Math.min(clip.effectiveDuration, clip.fadeInS ?? 0))
        if (fadeSec <= 0) return null
        const fadePx = Math.min(width, fadeSec * zoomLevel)
        return (
          <div
            className="absolute top-0 left-0 h-full pointer-events-none z-[5]"
            style={{
              width: `${fadePx}px`,
              background:
                'linear-gradient(to right, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)',
            }}
            data-testid="timeline-clip-fade-in-overlay"
            data-fade-in-s={fadeSec.toFixed(2)}
            data-fade-in-px={fadePx.toFixed(0)}
            aria-hidden="true"
            title={`Fade in: ${fadeSec.toFixed(1)}s`}
          />
        )
      })()}

      {/* Fade-out visualization overlay -- right edge, opacity ramp to black.
          Width = fadeOutS * zoomLevel (px/sec). Capped at clip width. */}
      {(() => {
        const fadeSec = Math.max(0, Math.min(clip.effectiveDuration, clip.fadeOutS ?? 0))
        if (fadeSec <= 0) return null
        const fadePx = Math.min(width, fadeSec * zoomLevel)
        return (
          <div
            className="absolute top-0 right-0 h-full pointer-events-none z-[5]"
            style={{
              width: `${fadePx}px`,
              background:
                'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 100%)',
            }}
            data-testid="timeline-clip-fade-out-overlay"
            data-fade-out-s={fadeSec.toFixed(2)}
            data-fade-out-px={fadePx.toFixed(0)}
            aria-hidden="true"
            title={`Fade out: ${fadeSec.toFixed(1)}s`}
          />
        )
      })()}

      {/* Label + duration overlay on top of filmstrip */}
      <div className="relative h-full flex flex-col justify-end px-2 py-1 pointer-events-none z-10">
        <div className="flex items-center gap-2">
          <span className="text-foreground text-xs font-medium truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
            {clip.label}
          </span>
          <span className="text-foreground text-xs tabular-nums drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
            {formatDuration(clip.effectiveDuration)}
          </span>
          {/* Speed badge */}
          {clip.speed !== 1.0 && (
            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-500/80 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
              {clip.speed}x
            </span>
          )}
          {/* Mute badge */}
          {clip.muted && (
            <span
              className="text-xs font-bold px-1.5 py-0.5 rounded bg-red-500/80 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
              data-testid="timeline-video-clip-mute-badge"
              title="Audio muted"
            >
              M
            </span>
          )}
        </div>
      </div>

      {/* Left edge resize handle */}
      {!locked && (
        <div
          className="absolute left-0 top-0 h-full w-2 cursor-ew-resize hover:bg-blue-400/50 transition-colors group"
          style={{ width: `${EDGE_RESIZE_WIDTH}px` }}
          onMouseDown={(e) => handleEdgeMouseDown(e, 'start')}
        >
          {/* Visual indicator on hover */}
          <div className="absolute inset-y-0 left-0 w-1 bg-blue-500/0 group-hover:bg-blue-500/70 transition-colors" />
        </div>
      )}

      {/* Right edge resize handle */}
      {!locked && (
        <div
          className="absolute right-0 top-0 h-full w-2 cursor-ew-resize hover:bg-blue-400/50 transition-colors group"
          style={{ width: `${EDGE_RESIZE_WIDTH}px` }}
          onMouseDown={(e) => handleEdgeMouseDown(e, 'end')}
        >
          {/* Visual indicator on hover */}
          <div className="absolute inset-y-0 right-0 w-1 bg-blue-500/0 group-hover:bg-blue-500/70 transition-colors" />
        </div>
      )}
    </div>
  )
})

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`
  }
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Audio waveform overlay for a video clip — sits at the bottom 30% of the
 * clip rectangle and reflects the clip's volume/muted state via amplitude.
 */
const WaveformOverlay = memo(function WaveformOverlay({
  clip,
  stripRatio = 0.3,
}: { clip: TimelineClipData; stripRatio?: number }) {
  const amplitude = clip.muted ? 0 : Math.max(0, Math.min(1, clip.volume))
  useEffect(() => {
    debug(
      'video-waveform amplitude updated: clipId=%s volume=%f muted=%s amplitude=%f',
      clip.id,
      clip.volume,
      clip.muted,
      amplitude,
    )
  }, [clip.id, clip.volume, clip.muted, amplitude])

  return (
    <div
      className="absolute left-0 right-0 bottom-0 pointer-events-none z-[5] bg-black/30 text-primary/70"
      style={{ height: `${Math.round(stripRatio * 100)}%` }}
    >
      <StaticWaveform sourcePath={clip.sourcePath} barCount={64} amplitude={amplitude} />
    </div>
  )
})

declare global {
  // RFC-0651 #2: shared trim-active flag (see TimelineEditor.handleTimelineClick).
  interface Window {
    __dwTimelineTrimActive?: boolean
  }
}
